
import "./customPieChart.scss";
import React, { useCallback, useState, useContext, useMemo } from "react";
import {
    PieChart,
    Pie,
    Sector,
    ResponsiveContainer,
    Cell,
    Tooltip
} from "recharts";
import {
    Typography
} from "antd";
import {
    PieChartOutlined
} from "@ant-design/icons";
import { DarkModeContext } from "../../../shared/contexts/darkModeContext";
import {useChartStatistics} from "../useSatistics";
import ErrorPage from "../../../shared/components/ErrorPage";
import IsLoading from "../../../shared/components/IsLoading";

const { Text } = Typography;

// 차트 색상 팔레트
const COLORS = [
    '#1890ff', '#52c41a', '#fa8c16', '#eb2f96',
    '#722ed1', '#13c2c2', '#faad14', '#f5222d',
    '#a0d911', '#2f54eb', '#fa541c', '#d4b106'
];

const CustomPieChart = ({
                            title,
                            type,
                            height = 300
                        }) => {
    const { darkMode } = useContext(DarkModeContext);
    const {data, isLoading, error, refetch} = useChartStatistics(type);

    const [activeIndex, setActiveIndex] = useState(0);

    const onPieEnter = useCallback((_, index) => {
        setActiveIndex(index);
    }, []);

    // 데이터 처리 및 정렬
    const processedData = useMemo(() => {
        if (!data || data.length === 0) return [];

        if (type === 'userLevel') {
            return data[0].map(({lv, user_count}, index) => {
                return {
                    name: lv,
                    Total: user_count,
                    color: COLORS[index % COLORS.length]
                }
            })
        } else {
            return data
                .map((item, index) => ({
                    ...item,
                    color: COLORS[index % COLORS.length]
                }))
                .sort((a, b) => b.Total - a.Total);
        }
    }, [data, type]);

    // 총합 계산
    const totalValue = useMemo(() => {
        return processedData.reduce((sum, item) => sum + item.Total, 0);
    }, [processedData]);

    // 활성 섹터 렌더링
    const renderActiveShape = (props) => {
        const RADIAN = Math.PI / 180;
        const {
            cx, cy, midAngle, innerRadius, outerRadius,
            startAngle, endAngle, fill, payload, percent, value
        } = props;

        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 22;
        const ey = my;
        const textAnchor = cos >= 0 ? 'start' : 'end';

        return (
            <g>
                {/* 중앙 라벨 */}
                <text
                    x={cx}
                    y={cy}
                    dy={8}
                    textAnchor="middle"
                    fill={darkMode ? '#fff' : '#333'}
                    fontSize="12"
                    fontWeight="600"
                >
                    {payload.name}
                </text>

                {/* 메인 섹터 */}
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />

                {/* 활성 섹터 하이라이트 */}
                <Sector
                    cx={cx}
                    cy={cy}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    innerRadius={outerRadius + 6}
                    outerRadius={outerRadius + 10}
                    fill={fill}
                />

                {/* 연결선 */}
                <path
                    d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
                    stroke={fill}
                    fill="none"
                    strokeWidth="1.5"
                />
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />

                {/* 라벨 텍스트 */}
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                    y={ey}
                    textAnchor={textAnchor}
                    fill={darkMode ? '#fff' : '#333'}
                    fontSize="11"
                    fontWeight="600"
                >
                    {`Lv${payload.name}: ${value?.toLocaleString()}`}
                </text>
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                    y={ey}
                    dy={16}
                    textAnchor={textAnchor}
                    fill={darkMode ? '#999' : '#666'}
                    fontSize="10"
                >
                    {`(${(percent * 100).toFixed(1)}%)`}
                </text>
            </g>
        );
    };

    // 커스텀 툴팁
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const percentage = totalValue > 0 ? (data.Total / totalValue * 100) : 0;

            return (
                <div className={`pie-tooltip ${darkMode ? 'dark' : 'light'}`}>
                    <div className="tooltip-header">
                        <div
                            className="color-indicator"
                            style={{ backgroundColor: data.color }}
                        />
                        <Text strong>Lv{data.name}</Text>
                    </div>
                    <div className="tooltip-content">
                        <Text>값: {data.Total?.toLocaleString()}</Text>
                        <Text>비율: {percentage?.toFixed(2)}%</Text>
                    </div>
                </div>
            );
        }
        return null;
    };

    if (processedData.length === 0) {
        return (
            <div className={`pie-widget ${darkMode ? 'dark-theme' : 'light-theme'}`}>
                <div className="widget-header">
                    <PieChartOutlined className="widget-icon" />
                    <Text className="widget-title">{title}</Text>
                </div>
                <div className="widget-empty">
                    <PieChartOutlined className="empty-icon" />
                    <Text type="secondary">데이터가 없습니다</Text>
                </div>
            </div>
        );
    }

    if (error) return <ErrorPage message={error} />;
    if (isLoading) return <IsLoading />;

    return (
        <div className={`pie-widget ${darkMode ? 'dark-theme' : 'light-theme'}`}>
            <div className="widget-header">
                <PieChartOutlined className="widget-icon" />
                <Text className="widget-title">{title}</Text>
                <Text className="widget-subtitle">총 {totalValue.toLocaleString()}</Text>
            </div>

            <div className="widget-chart" style={{ height: height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={processedData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            dataKey="Total"
                            onMouseEnter={onPieEnter}
                        >
                            {processedData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default CustomPieChart;